import AccountModule from "../store/modules/account";
import AppModule, {myDebug} from "../store/modules/app";

export default function RightsModule() {
    const accountModule = AccountModule();
    const appModule = AppModule();
    const rights_masks = appModule.rights_masks.value;
    const ACCOUNTS_INFO = accountModule.available_accounts.value;
    const ADMIN_ACCOUNT = rights_masks.admin_account;
    const MANAGE_ACCOUNT = rights_masks.manage_account;
    const DELETE_CLIENT = rights_masks.delete_client;
    const MANAGE_CLIENT = rights_masks.manage_client;
    const ADMIN_CLIENT = rights_masks.admin_client;

    // Note: do take care when using these methods
    // For example when adding a new client, the client is not updated to ACCOUNTS_INFO
    // afterwards using can_edit_client will return false, even if it should return true
    // Only hard refresh would help

    const get_client_rights = client_id => {
        if (!client_id) return 0;
        let clientRights = 0

        ACCOUNTS_INFO.forEach(row => {
            row.clients.forEach(client => {
                if (parseInt(client.id) === parseInt(client_id)) {
                    clientRights = client.rights;
                }
            });
        });
        return clientRights
    };
    const get_account_rights = account_id => {
        if (!account_id) return 0;
        const account_info = ACCOUNTS_INFO.find(a => parseInt(a.id) === parseInt(account_id));
        if (account_info && account_info.rights) {
            return account_info.rights;
        }
        return 0;
    };
    const can_delete_account = account_id => {
        const rights = get_account_rights(account_id);
        return (rights & ADMIN_ACCOUNT) > 0;
    };

    const is_owner = account_id => {
        const rights = get_account_rights(account_id);
        return (rights & ADMIN_ACCOUNT) > 0;
    };

    const can_edit_owner_locked_settings = account => {
        if (is_owner(account.id)) {
            return true
        }

        return !is_setup_enabled('account', 'disable_company_settings_edit_for_all_members', account)
    }

    const can_edit_account = account_id => {
        const rights = get_account_rights(account_id);
        return (rights & MANAGE_ACCOUNT) === MANAGE_ACCOUNT;
    };

    const can_edit_email_integrations_enabled = account_id => {
        const rights = get_account_rights(account_id);
        return (rights & MANAGE_ACCOUNT) === MANAGE_ACCOUNT;
    };

    const can_delete_client = client_id => {
        const rights = get_client_rights(client_id);
        return (rights & DELETE_CLIENT) > 0;
    };
    const can_edit_client = client_id => {
        const rights = get_client_rights(client_id);
        return (rights & MANAGE_CLIENT) > 0;
    };
    const is_client_admin = client_id => {
        const rights = get_client_rights(client_id);
        return (rights & ADMIN_CLIENT) > 0;
    };
    const has_right = (rights, right_mask) => {
        return (rights & right_mask) > 0;
    };

    // Check if key is enabled
    const is_key_enabled = (obj, key, container = null) => {
        let result = false
        if (container) {
            result = obj && key && obj.data
                && (container in obj.data) && obj.data.container && (key in obj.data.container)
                && (obj.data.container[key] === 1 || obj.data[key] === true)
        } else {
            result = obj && key && obj.data && (key in obj.data)
                && (obj.data[key] === 1 || obj.data[key] === true)
        }
        return !!result
    }
    // Generic object settings enabled check
    const is_setup_enabled = (feature, key, obj, section = null) => {
        // future todo :: "account" and "client" should also be features
        // future todo :: implement storage for generic feature settings
        /*
        Example :: "account_feature_settings"."data"."setup" = {'read': 1, 'write': 0, 'delete': 0}
        if (feature == 'billing') {
            const container = 'setup'
            return is_key_enabled(obj, key, container)
        }
         */
        return !!is_key_enabled(obj, key, section)
    }

    // Billing helpers
    const billing_enabled = (account) => {
        return accountModule.isFeatureEnabled(account.id, "billing", false)
    }

    const is_admin_or_owner = (account) => {
        return can_edit_account(account.id)
    }

    const is_owner_of_account = account => {
        const rights = get_account_rights(account.id);
        return (rights & ADMIN_ACCOUNT) > 0;
    }


    // Billing feature rights
    const can_see_billing_menu = () => {
        return ACCOUNTS_INFO.some(account => can_see_account_billing(account))
    }

    const can_see_account_billing = (account) => {
        const billing_rights = account.billing_settings || {}

        return billing_enabled(account)
            && (
                is_admin_or_owner(account)
                || billing_rights.is_user_able_to_manage_contracts
                || billing_rights.is_invoice_management_enabled_for_users
                || billing_rights.is_client_invoices_visible_to_users
            )
    }

    const can_see_client_billing = (account, client_id) => {
        const condition = can_see_client_invoices(account, client_id) || can_see_client_contracts(account, client_id)

        if (myDebug.level() >= 4) console.warn('* can_see_client_billing', condition)
        return (condition || is_admin_or_owner(account)) && billing_enabled(account)
    }

    const can_see_client_contracts = (account, client_id) => {
        const condition = can_manage_client_contracts_and_invoices(account, client_id)

        if (myDebug.level() >= 4) console.warn('* can_see_client_contracts', condition)
        return (condition || is_admin_or_owner(account)) && billing_enabled(account)
    }

    const can_manage_client_contracts_and_invoices = (account, client_id) => {
        const billing_rights = account.billing_settings || {}

        const condition = is_client_admin(client_id) && billing_rights.is_user_able_to_manage_contracts

        if (myDebug.level() >= 4) console.warn('* can_manage_client_contracts_and_invoices', condition)
        return (condition || is_admin_or_owner(account)) && billing_enabled(account)
    }

    const can_see_client_invoices = (account, client_id) => {
        const billing_rights = account.billing_settings || {}

        const condition =
            billing_rights.is_client_invoices_visible_to_users
            || billing_rights.is_invoice_management_enabled_for_users
            || can_manage_client_contracts_and_invoices(account, client_id)

        if (myDebug.level() >= 4) console.warn('* can_see_client_invoices', condition)
        return (condition || is_admin_or_owner(account)) && billing_enabled(account)
    }

    const can_edit_products = (account) => {
        const billing_rights = account.billing_settings || {}

        const condition = billing_rights.allow_only_owners_to_add_and_update_products
        if (condition) {
            return is_owner_of_account(account)
        } else {
            return is_admin_or_owner(account)
        }
    }

    const can_manage_client_invoices = (account, client_id) => {
        const billing_rights = account.billing_settings || {}

        const condition =
            billing_rights.is_invoice_management_enabled_for_users
            || can_manage_client_contracts_and_invoices(account, client_id)

        if (myDebug.level() >= 4) console.warn('* can_manage_client_invoices', condition)
        return (condition || is_admin_or_owner(account)) && billing_enabled(account)
    }

    const can_complete_invoice_rows = (account) => {
        const billing_rights = account.billing_settings || {}

        // this is a visual feature, requiring enabled other rights
        const condition = billing_rights.can_complete_invoice_rows

        if (myDebug.level() >= 4) console.warn('* can_complete_invoice_rows', condition)
        return condition
    }

    const can_send_invoices_to_clients = (account) => {
        const billing_rights = account.billing_settings || {}

        // this is a visual feature, requiring enabled other rights
        const condition = billing_rights.allow_members_to_send_invoices_to_clients

        if (myDebug.level() >= 4) console.warn('* allow_members_to_send_invoices_to_clients', condition)
        return condition
    }

    const can_edit_invoice_rows = (account, client_id) => {
        return is_admin_or_owner(account) || is_client_admin(client_id)
    }

    return {
        get_client_rights,
        get_account_rights,
        can_delete_account,
        can_edit_account,
        is_owner,
        can_delete_client,
        can_edit_client,
        is_client_admin,
        has_right,

        can_see_billing_menu,
        can_see_account_billing,
        can_see_client_billing,
        can_see_client_contracts,
        can_manage_client_contracts_and_invoices,
        can_manage_client_invoices,
        can_see_client_invoices,

        can_complete_invoice_rows,
        can_send_invoices_to_clients,
        is_key_enabled,
        is_setup_enabled,
        is_admin_or_owner,
        can_edit_invoice_rows,
        can_edit_products,
        can_edit_owner_locked_settings
    };
}
